// src/utils/ucpDialer.js

const NUMBER_INSERT_BUTTON_XPATH = '/html/body/div[2]/div/main/div/div[2]/aside/div[2]/div[1]/i';
const NUMBER_INSERT_INPUT_XPATH = '/html/body/div[2]/div/main/div/div[2]/aside/div[2]/div[1]/input';
const NUMBER_CALL_BUTTON_XPATH = '/html/body/div[2]/div/main/div/div[2]/aside/div[2]/div[1]/div/button/i';
const NUMBER_INSERT_BUTTON_SELECTOR = '#main-content > div > div.sc-dTGSLY.jZZrjP > aside > div.sc-cittYi.iEYiNU > div.search-option > i';
const NUMBER_INSERT_INPUT_SELECTOR = '#main-content > div > div.sc-dTGSLY.jZZrjP > aside > div.sc-jcVbNL.elVUfg > div.search-contacts-box > input[type=text]';
const NUMBER_CALL_BUTTON_SELECTOR = '#main-content > div > div.sc-dTGSLY.jZZrjP > aside > div.sc-jcVbNL.elVUfg > div.search-contacts-box > div > button > i';
export const UCP_DEMO_LOGIN_URL = 'https://ucdemo.voicemeetme.com/ucp/login';
export const UCP_IN_LOGIN_URL = 'https://ucpin.voicemeetme.com/ucp/login';
export const UCP_MED_LOGIN_URL = 'https://ucpmed.voicemeetme.com/ucp/login';
export const UCP_OCUBE_LOGIN_URL = 'https://uc.ocubeservices.com/ucp/login';

const DIAL_REQUEST_DEDUPE_MS = 1500;
let lastDialRequest = { destination: '', timestamp: 0 };

const getAccessibleDocuments = () => {
  const docs = [];
  const addDocument = (doc) => {
    if (doc && !docs.includes(doc)) {
      docs.push(doc);
    }
  };

  const addWindowDocuments = (targetWindow) => {
    if (!targetWindow) return;

    try {
      addDocument(targetWindow.document);

      Array.from(targetWindow.frames || []).forEach((frame) => {
        try {
          addWindowDocuments(frame);
        } catch (error) {
          // Nested frame may be cross-origin.
        }
      });
    } catch (error) {
      // Window may be cross-origin when CRM is embedded.
    }
  };

  addWindowDocuments(window);
  addWindowDocuments(window.parent);
  addWindowDocuments(window.top);

  return docs;
};

const findByXPath = (doc, xpath) => {
  try {
    return doc.evaluate(xpath, doc, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue;
  } catch (error) {
    return null;
  }
};

const findBySelector = (doc, selector) => {
  try {
    return doc.querySelector(selector);
  } catch (error) {
    return null;
  }
};

const findDialerElement = (doc, xpath, selector) => (
  findBySelector(doc, selector) || findByXPath(doc, xpath)
);

const hasDialerControls = (doc) => (
  findDialerElement(doc, NUMBER_INSERT_BUTTON_XPATH, NUMBER_INSERT_BUTTON_SELECTOR)
  || findDialerElement(doc, NUMBER_INSERT_INPUT_XPATH, NUMBER_INSERT_INPUT_SELECTOR)
  || findDialerElement(doc, NUMBER_CALL_BUTTON_XPATH, NUMBER_CALL_BUTTON_SELECTOR)
);

const setInputValue = (input, value) => {
  const valueSetter = Object.getOwnPropertyDescriptor(input, 'value')?.set;
  const prototype = Object.getPrototypeOf(input);
  const prototypeValueSetter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set;

  if (prototypeValueSetter && valueSetter !== prototypeValueSetter) {
    prototypeValueSetter.call(input, value);
  } else if (valueSetter) {
    valueSetter.call(input, value);
  } else {
    input.value = value;
  }

  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
};

const clickElement = (element) => {
  const clickable = element?.closest?.('button') || element;
  clickable?.click?.();
};

const notifyHostDialer = (destination) => {
  const now = Date.now();
  const message = {
    type: 'CRM_DIAL_CUSTOMER',
    requestId: `${destination}-${now}`,
    destination,
    xpaths: {
      numberInsertButton: NUMBER_INSERT_BUTTON_XPATH,
      numberInsertInput: NUMBER_INSERT_INPUT_XPATH,
      numberCallButton: NUMBER_CALL_BUTTON_XPATH
    },
    selectors: {
      numberInsertButton: NUMBER_INSERT_BUTTON_SELECTOR,
      numberInsertInput: NUMBER_INSERT_INPUT_SELECTOR,
      numberCallButton: NUMBER_CALL_BUTTON_SELECTOR
    }
  };

  let targetWindow = null;

  try {
    if (window.parent && window.parent !== window) {
      targetWindow = window.parent;
    }
  } catch (error) {
    // Parent may not be available.
  }

  try {
    if (!targetWindow && window.top && window.top !== window) {
      targetWindow = window.top;
    }
  } catch (error) {
    // Top window may not be available.
  }

  if (!targetWindow) {
    console.warn('[CRM Dialer] No host window available for dial request.', { destination });
    return;
  }

  try {
    targetWindow.postMessage(message, '*');
    console.info('[CRM Dialer] Host dial request sent exactly once.', {
      destination,
      requestId: message.requestId
    });
  } catch (error) {
    // Target window may not be available.
  }
};

const callPopupDialer = (destination) => {
  if (typeof window.makeCall !== 'function') {
    return false;
  }

  try {
    return window.makeCall(destination);
  } catch (error) {
    console.warn('[CRM Dialer] UCP popup makeCall failed. Falling back to DOM dialing.', error);
    return false;
  }
};

export const normalizeDialPhone = (value) => {
  if (!value) return '';
  const raw = String(value).trim();
  if (!raw) return '';
  const hasPlus = raw.startsWith('+');
  const digits = raw.replace(/\D/g, '');
  return hasPlus ? `+${digits}` : digits;
};

export const dialCustomerPhone = (phoneNumber) => {
  const destination = normalizeDialPhone(phoneNumber);

  if (!destination) {
    alert('No phone number available for this record.');
    return false;
  }

  const now = Date.now();
  if (
    lastDialRequest.destination === destination
    && now - lastDialRequest.timestamp < DIAL_REQUEST_DEDUPE_MS
  ) {
    console.info('[CRM Dialer] Duplicate dial request blocked at source.', { destination });
    return true;
  }
  lastDialRequest = { destination, timestamp: now };

  if (callPopupDialer(destination)) {
    return true;
  }

  const dialerDocument = getAccessibleDocuments().find(hasDialerControls);

  if (!dialerDocument) {
    console.info('[CRM Dialer] Dialer DOM is not directly accessible. Sending request to host page.', { destination });
    notifyHostDialer(destination);
    return true;
  }

  console.info('[CRM Dialer] Dialer DOM found. Filling number directly.', { destination });

  const insertButton = findDialerElement(dialerDocument, NUMBER_INSERT_BUTTON_XPATH, NUMBER_INSERT_BUTTON_SELECTOR);
  clickElement(insertButton);

  window.setTimeout(() => {
    const input = findDialerElement(dialerDocument, NUMBER_INSERT_INPUT_XPATH, NUMBER_INSERT_INPUT_SELECTOR);
    const callButton = findDialerElement(dialerDocument, NUMBER_CALL_BUTTON_XPATH, NUMBER_CALL_BUTTON_SELECTOR);

    if (!input || !callButton) {
      console.warn('[CRM Dialer] Dialer input or call button not found after opening search box.', {
        inputFound: !!input,
        callButtonFound: !!callButton
      });
      alert('Dialer input or call button was not found on this screen.');
      return;
    }

    input.focus?.();
    setInputValue(input, destination);
    clickElement(callButton);
  }, 100);

  return true;
};
